import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { subscriptionService } from '../../services/subscriptionService';
import NavigationSidebar from '../../components/NavigationSidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import CurrentPlanOverview from './components/CurrentPlanOverview';
import PaymentHistory from './components/PaymentHistory';
import PlanManagement from './components/PlanManagement';
import BillingInformation from './components/BillingInformation';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

const stripePromise = loadStripe(import.meta.env?.VITE_STRIPE_PUBLISHABLE_KEY);

const BillingManagement = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');

  useEffect(() => {
    if (user?.id) {
      loadData();
    } else {
      setLoading(false);
    }
  }, [user]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [subResult, plansResult] = await Promise.all([
        subscriptionService?.getUserSubscription(user?.id),
        subscriptionService?.getPlans()
      ]);
      setSubscription(subResult?.data);
      setPlans(plansResult?.data || []);
    } catch (error) {
      console.error('Error loading billing data:', error);
      setSubscription(null);
      setPlans([]);
    } finally {
      setLoading(false);
    }
  };

  const sections = [
    { id: 'overview', label: 'Current Plan', icon: 'CreditCard' },
    { id: 'history', label: 'Payment History', icon: 'Receipt' },
    { id: 'plans', label: 'Plan Management', icon: 'Package' },
    { id: 'billing', label: 'Billing Info', icon: 'FileText' }
  ];

  if (loading) {
    return (
      <div className="flex h-screen bg-background">
        <NavigationSidebar />
        <div className="flex-1 flex items-center justify-center ml-0 lg:ml-60">
          <p className="text-muted-foreground">Loading billing information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background">
      <NavigationSidebar />
      <main className="flex-1 ml-0 lg:ml-60 pb-20 lg:pb-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground font-heading mb-2">
                Billing Management
              </h1>
              <p className="text-muted-foreground">
                Manage your subscription, payment methods, and billing information
              </p>
            </div>
            <Button
              variant="outline"
              size="md"
              onClick={() => navigate('/dashboard')}
              iconName="ArrowLeft"
              iconPosition="left"
            >
              Back to Dashboard
            </Button>
          </div>

          {/* Section Navigation */}
          <div className="bg-card border border-border rounded-xl p-2">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
              {sections?.map((section) => (
                <button
                  key={section?.id}
                  onClick={() => setActiveSection(section?.id)}
                  className={`
                    flex items-center justify-center gap-2 px-4 py-3 rounded-lg
                    transition-all duration-200 font-medium text-sm
                    ${activeSection === section?.id
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    }
                  `}
                >
                  <Icon name={section?.icon} size={18} />
                  <span className="hidden sm:inline">{section?.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Content Sections */}
          <div className="space-y-6">
            {activeSection === 'overview' && (
              <CurrentPlanOverview 
                subscription={subscription} 
                onUpgrade={() => setActiveSection('plans')}
              />
            )}
            {activeSection === 'history' && (
              <PaymentHistory subscription={subscription} />
            )}
            {activeSection === 'plans' && (
              <PlanManagement 
                subscription={subscription} 
                plans={plans}
                onPlanChange={loadData}
              />
            )}
            {activeSection === 'billing' && (
              <Elements stripe={stripePromise}>
                <BillingInformation subscription={subscription} />
              </Elements>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default BillingManagement;